import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  FaCalendarAlt,
  FaChartBar,
  FaTimes,
  FaFilePdf,
  FaArrowRight,
  FaHistory,
  FaImage,
} from "react-icons/fa";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const PrintChoiceModal = ({
  isOpen,
  onClose,
  party,
  month,
  year,
  onSelectReportType,
  initialIncludeHistory = true,
  initialIncludeImages = true,
}) => {
  const [includeHistory, setIncludeHistory] = useState(initialIncludeHistory);
  const [includeImages, setIncludeImages] = useState(initialIncludeImages);

  useEffect(() => {
    setIncludeHistory(initialIncludeHistory);
    setIncludeImages(initialIncludeImages);
  }, [initialIncludeHistory, initialIncludeImages, isOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose?.();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !party) return null;

  const partyName = party?.CompanyName || "Party";
  const monthName = MONTH_NAMES[month] || "";

  const handleSelect = (type) => {
    onSelectReportType?.(type, { includeHistory, includeImages });
  };

  const modalContent = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/75 backdrop-blur-xs animate__animated animate__fadeIn animate__faster"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose?.();
        }
      }}
    >
      <div
        className="bg-white w-full max-w-lg rounded-2xl shadow-2xl border border-slate-200 overflow-hidden cursor-default pointer-events-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="bg-[#154D75] text-white px-5 py-3.5 flex items-center justify-between shadow-md">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center text-white shrink-0">
              <FaFilePdf className="text-sm" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-white tracking-wide">
                Choose Report Type to Print / Export
              </h3>
              <p className="text-[11px] text-blue-100/80 truncate">
                {partyName} &bull; Year {year}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-white/80 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
            title="Close"
          >
            <FaTimes className="text-base" />
          </button>
        </div>

        {/* Modal Body: 3 Choices as Accessible Buttons */}
        <div className="p-5 space-y-3">
          {/* Print Content Customization: History & Images */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-slate-800">
                Print Content Inclusions:
              </span>
              <span className="text-[10px] text-slate-500 font-medium">
                Pehle select karein kya print karna hai
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {/* Option 1: Assignment & Forwarding History */}
              <div className="bg-white border border-slate-200 rounded-lg p-2 flex flex-col justify-between shadow-2xs">
                <div className="mb-1.5">
                  <div className="text-[11px] font-bold text-slate-900 flex items-center gap-1.5">
                    <FaHistory className="text-[#154D75] text-[10px]" />
                    <span>Assignment History</span>
                  </div>
                  <p className="text-[9.5px] text-slate-500 mt-0.5 leading-tight">
                    Forwarding timeline &amp; remarks
                  </p>
                </div>
                <div className="inline-flex rounded-lg bg-slate-100 p-0.5 border border-slate-200 text-[10px]">
                  <button
                    type="button"
                    onClick={() => setIncludeHistory(true)}
                    className={`flex-1 py-1 rounded font-bold transition-all cursor-pointer ${
                      includeHistory
                        ? "bg-[#154D75] text-white shadow-2xs"
                        : "text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    ✓ With History
                  </button>
                  <button
                    type="button"
                    onClick={() => setIncludeHistory(false)}
                    className={`flex-1 py-1 rounded font-bold transition-all cursor-pointer ${
                      !includeHistory
                        ? "bg-[#154D75] text-white shadow-2xs"
                        : "text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    ✕ Without History
                  </button>
                </div>
              </div>

              {/* Option 2: Call Photo Attachments (JPG / PNG) */}
              <div className="bg-white border border-slate-200 rounded-lg p-2 flex flex-col justify-between shadow-2xs">
                <div className="mb-1.5">
                  <div className="text-[11px] font-bold text-slate-900 flex items-center gap-1.5">
                    <FaImage className="text-emerald-700 text-[10px]" />
                    <span>Photos (JPG / PNG)</span>
                  </div>
                  <p className="text-[9.5px] text-slate-500 mt-0.5 leading-tight">
                    Calls me judi photo attachments
                  </p>
                </div>
                <div className="inline-flex rounded-lg bg-slate-100 p-0.5 border border-slate-200 text-[10px]">
                  <button
                    type="button"
                    onClick={() => setIncludeImages(true)}
                    className={`flex-1 py-1 rounded font-bold transition-all cursor-pointer ${
                      includeImages
                        ? "bg-emerald-700 text-white shadow-2xs"
                        : "text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    ✓ With Images
                  </button>
                  <button
                    type="button"
                    onClick={() => setIncludeImages(false)}
                    className={`flex-1 py-1 rounded font-bold transition-all cursor-pointer ${
                      !includeImages
                        ? "bg-slate-700 text-white shadow-2xs"
                        : "text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    ✕ Without Images
                  </button>
                </div>
              </div>
            </div>
          </div>

          <p className="text-xs text-slate-600 font-medium">
            Kripya select karein ki aapko kis format me Party Call Report print ya PDF export karni hai:
          </p>

          {/* Option 1: Selected Month Report */}
          <button
            type="button"
            onClick={() => handleSelect("month")}
            className="w-full text-left group p-3.5 rounded-xl border-2 border-slate-200 hover:border-[#154D75] bg-white hover:bg-blue-50/50 transition-all cursor-pointer shadow-2xs hover:shadow-md flex items-start gap-3.5"
          >
            <div className="w-10 h-10 rounded-xl bg-blue-100 text-[#154D75] group-hover:bg-[#154D75] group-hover:text-white flex items-center justify-center shrink-0 transition-colors mt-0.5 shadow-2xs">
              <FaCalendarAlt className="text-lg" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-1 mb-1">
                <h4 className="text-xs sm:text-sm font-bold text-slate-900 group-hover:text-[#154D75] transition-colors">
                  1. Selected Month Report &ndash; {monthName} {year}
                </h4>
                <span className="text-[10px] font-semibold text-[#154D75] bg-blue-50 px-2 py-0.5 rounded border border-blue-200 shrink-0">
                  Calendar + Tasks
                </span>
              </div>
              <p className="text-[11px] text-slate-600 leading-relaxed">
                First Page par <strong>{monthName} {year} ka Complete Calendar</strong> aur <strong>Monthly Summary</strong>, followed by is month ke <strong>Date-wise task calls</strong>.
              </p>
              <div className="mt-2 flex items-center gap-1 text-[11px] font-bold text-[#154D75] group-hover:underline">
                <span>View &amp; Print Selected Month</span>
                <FaArrowRight className="text-[9px] group-hover:translate-x-0.5 transition-transform" />
              </div>
            </div>
          </button>

          {/* Option 2: All Months Calendar & Summary (Pure Year) */}
          <button
            type="button"
            onClick={() => handleSelect("yearly-calendar")}
            className="w-full text-left group p-3.5 rounded-xl border-2 border-slate-200 hover:border-blue-600 bg-white hover:bg-blue-50/60 transition-all cursor-pointer shadow-2xs hover:shadow-md flex items-start gap-3.5 ring-1 ring-blue-500/20"
          >
            <div className="w-10 h-10 rounded-xl bg-[#154D75] text-white group-hover:bg-[#0f3856] flex items-center justify-center shrink-0 transition-colors mt-0.5 shadow-2xs">
              <FaCalendarAlt className="text-lg" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-1 mb-1">
                <h4 className="text-xs sm:text-sm font-bold text-slate-900 group-hover:text-[#154D75] transition-colors">
                  2. All Months Calendar &amp; Summary &ndash; Pure Year {year}
                </h4>
                <span className="text-[10px] font-bold text-white bg-[#154D75] px-2 py-0.5 rounded shadow-2xs shrink-0">
                  1st Page Index + Active Months
                </span>
              </div>
              <p className="text-[11px] text-slate-600 leading-relaxed">
                <strong>1st Main Page</strong> par poora <strong>Master Index Table</strong> (konse page par kaunsa month hai) + jis jis month me call data hai sirf unhi ka <strong>Complete Calendar &amp; Monthly Summary</strong> (Khali months print me nahi aayenge).
              </p>
              <div className="mt-2 flex items-center gap-1 text-[11px] font-bold text-[#154D75] group-hover:underline">
                <span>View &amp; Print All Months Report (Index Page + Active Calendars)</span>
                <FaArrowRight className="text-[9px] group-hover:translate-x-0.5 transition-transform" />
              </div>
            </div>
          </button>

          {/* Option 3: Annual 12-Month KPI Summary Statement */}
          <button
            type="button"
            onClick={() => handleSelect("annual-statement")}
            className="w-full text-left group p-3.5 rounded-xl border-2 border-slate-200 hover:border-emerald-600 bg-white hover:bg-emerald-50/40 transition-all cursor-pointer shadow-2xs hover:shadow-md flex items-start gap-3.5"
          >
            <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 group-hover:bg-emerald-600 group-hover:text-white flex items-center justify-center shrink-0 transition-colors mt-0.5 shadow-2xs">
              <FaChartBar className="text-lg" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-1 mb-1">
                <h4 className="text-xs sm:text-sm font-bold text-slate-900 group-hover:text-emerald-800 transition-colors">
                  3. Annual KPI Summary Statement &ndash; Year {year}
                </h4>
                <span className="text-[10px] font-semibold text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200 shrink-0">
                  Single Sheet Overview
                </span>
              </div>
              <p className="text-[11px] text-slate-600 leading-relaxed">
                Poore 12 mahino ka <strong>Performance Table</strong> aur <strong>Annual KPI Overview Cards</strong> sirf 1 single sheet par.
              </p>
              <div className="mt-2 flex items-center gap-1 text-[11px] font-bold text-emerald-700 group-hover:underline">
                <span>View &amp; Print Annual KPI Statement (Single Sheet)</span>
                <FaArrowRight className="text-[9px] group-hover:translate-x-0.5 transition-transform" />
              </div>
            </div>
          </button>
        </div>

        {/* Modal Footer */}
        <div className="bg-slate-50 px-5 py-3 border-t border-slate-200 flex items-center justify-between text-xs">
          <span className="text-slate-500 text-[11px]">
            Tip: Dono me se kisi bhi report ko preview me switch kar sakte hain.
          </span>
          <button
            type="button"
            onClick={onClose}
            className="px-3.5 py-1.5 rounded-lg bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold cursor-pointer transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default PrintChoiceModal;
